import { useEffect, useState } from "react";
import { apiClient } from "@apis/apiClient";
import type {
  User,
  UserOption,
  CreateUserParams,
  UpdateUserParams,
  UpdateUserProfileParams,
  ChangePasswordParams,
  RegisterUserParams,
  UserSearchParams,
  InviteUserParams,
  UserInvitation,
  ActivateInvitationParams,
  GetUsersPageParams,
  LanguageTranslations,
  RedirectResponse,
  TokenResponse,
  EmailVerificationResult,
  SuccessResponse,
  ApiResponse,
} from "./user.dto";

/**
 * User API Client
 *
 * Provides methods for interacting with user-related endpoints.
 * Uses DTOs from user.dto.ts for type safety.
 */
const userClient = {
  /**
   * Get language translations for a specific locale
   * @param locale - Language code (e.g., "en", "nl", "fr")
   * @returns Object with language key-value pairs
   */
  getLanguage(locale: string): Promise<ApiResponse<LanguageTranslations>> {
    return apiClient.get(`/rest/language/i18n?locale=${locale}`);
  },

  /**
   * Get original (non-customized) language translations for a specific locale
   * @param locale - Language code (e.g., "en", "nl", "fr")
   * @returns Object with language key-value pairs
   */
  getOriginalLanguage(
    locale: string
  ): Promise<ApiResponse<LanguageTranslations>> {
    return apiClient.get(`/rest/language/i18n/original?locale=${locale}`);
  },

  getRedirectExternalIdP(
    orgId: number,
    redirectTo?: string
  ): Promise<ApiResponse<RedirectResponse>> {
    return apiClient.get(`/rest/users/redirect/externalIdP/${orgId}`, {
      params: { redirectTo: redirectTo },
    });
  },

  getWrapperRedirectExternalIdP(
    orgId: number,
    redirectTo?: string
  ): Promise<ApiResponse<RedirectResponse>> {
    return apiClient.get(`/rest/users/redirect/externalIdP/wrapper/${orgId}`, {
      params: { redirectTo: redirectTo },
    });
  },

  impersonateUser(id: string): Promise<ApiResponse<SuccessResponse>> {
    return apiClient.put(`/rest/users/impersonate/${id}`);
  },

  impersonateAdminSupportUserByOrgId(
    orgId: number
  ): Promise<ApiResponse<SuccessResponse>> {
    return apiClient.put(`/rest/users/impersonate/admin/organisation/${orgId}`);
  },

  impersonateViewerSupportUserByOrgId(
    orgId: number
  ): Promise<ApiResponse<SuccessResponse>> {
    return apiClient.put(
      `/rest/users/impersonate/viewer/organisation/${orgId}`
    );
  },

  getPrevUserByTokens(
    token: string,
    csrf: string
  ): Promise<ApiResponse<TokenResponse>> {
    return apiClient.put(`/rest/users/token`, { token: token, csrf: csrf });
  },

  getDataByPage(params: GetUsersPageParams): Promise<ApiResponse<User[]>> {
    return apiClient.get("/rest/users", { params: params });
  },

  delete(id: string): Promise<ApiResponse<SuccessResponse>> {
    return apiClient.delete(`/rest/users/${id}`);
  },

  get(id: string): Promise<ApiResponse<User>> {
    return apiClient.get(`/rest/users/${id}`);
  },

  requestForAnVerifiedEmail(id: string): Promise<ApiResponse<SuccessResponse>> {
    return apiClient.get(`/rest/users/requestForAnVerificationEmail/${id}`);
  },

  forceVerifyEmailAddress(id: string): Promise<ApiResponse<SuccessResponse>> {
    return apiClient.get(`/rest/users/forceVerifyEmailAddress/${id}`);
  },

  create(orgId: number, params: CreateUserParams): Promise<ApiResponse<User>> {
    return apiClient.post(`/rest/users/?orgId=${orgId}`, params);
  },

  update(userId: string, params: UpdateUserParams): Promise<ApiResponse<User>> {
    return apiClient.put(`/rest/users/${userId}`, params);
  },

  unlockUserAccount(code: string): Promise<ApiResponse<SuccessResponse>> {
    return apiClient.put(`/rest/users/unlockAccount/${code}`);
  },

  changeImageUser(id: string, nameFile: string): Promise<ApiResponse<User>> {
    return apiClient.put(`/rest/users/changeImage/${id}/?image=${nameFile}`);
  },

  updateUserProfile(
    userId: string,
    params: UpdateUserProfileParams
  ): Promise<ApiResponse<User>> {
    return apiClient.put(`/rest/users/profile/${userId}`, params);
  },

  updateHideIntegratedHelp(
    userId: string,
    code: string
  ): Promise<ApiResponse<SuccessResponse>> {
    return apiClient.put(
      `/rest/users/profile/${userId}/hideIntegratedHelp/${code}`
    );
  },

  resetHideIntegratedHelp(
    userId: string
  ): Promise<ApiResponse<SuccessResponse>> {
    return apiClient.put(
      `/rest/users/profile/${userId}/hideIntegratedHelp/reset`
    );
  },

  changePassword(
    userID: string,
    params: ChangePasswordParams
  ): Promise<ApiResponse<SuccessResponse>> {
    return apiClient.post(`/rest/users/changePassword/${userID}`, params);
  },

  changeMyPassword(
    userID: string,
    params: ChangePasswordParams
  ): Promise<ApiResponse<SuccessResponse>> {
    return apiClient.post(`/rest/users/changeMyPassword/${userID}`, params);
  },

  register(
    userId: string,
    params: RegisterUserParams
  ): Promise<ApiResponse<User>> {
    return apiClient.put(`/rest/users/register/${userId}`, params);
  },

  verifyEmailCode(
    codeText: string
  ): Promise<ApiResponse<EmailVerificationResult>> {
    return apiClient.get(`/rest/users/verifyEmailCode?codeText=${codeText}`);
  },

  search(params: UserSearchParams): Promise<ApiResponse<User[]>> {
    return apiClient.post(`/rest/users/search`, params, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },

  getByOrg(orgId: number): Promise<ApiResponse<User[]>> {
    return apiClient.get(`/rest/users/getByOrg?orgId=${orgId}`);
  },

  getAll(): Promise<ApiResponse<User[]>> {
    return apiClient.get("/rest/users");
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  countUsersByFilters(params: any): Promise<ApiResponse<number>> {
    return apiClient.post("/rest/users/filter/count", JSON.stringify(params), {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },

  getContactsEmailByOrg(orgId?: number): Promise<ApiResponse<User[]>> {
    let uri = "/rest/users/getContactsByOrg";
    if (orgId) {
      uri += "?orgId=" + orgId;
    }
    return apiClient.get(uri);
  },

  invite(
    orgId: number,
    params: InviteUserParams
  ): Promise<ApiResponse<UserInvitation>> {
    return apiClient.post(`/rest/users/invite?orgId=${orgId}`, params);
  },

  getInvitedUserInfo(code: string): Promise<ApiResponse<UserInvitation>> {
    return apiClient.get(`/rest/users/invite/${code}`);
  },

  activateInvitation(
    params: ActivateInvitationParams
  ): Promise<ApiResponse<User>> {
    return apiClient.post(`/rest/users/invite/activate`, params);
  },

  resendInvitation(id: string): Promise<ApiResponse<SuccessResponse>> {
    return apiClient.post(`/rest/users/resendInvite/${id}`);
  },

  debounce(id: string): Promise<ApiResponse<SuccessResponse>> {
    return apiClient.put(`/rest/users/debounce/${id}`);
  },

  checkUserNameAvailability(userName: string): Promise<ApiResponse<boolean>> {
    return apiClient.get(
      `/rest/users/check-username-availability?userName=${userName}`
    );
  },
};

/**
 * Hook to get users by organization
 *
 * @param orgId - Organization ID
 * @returns Array of user options for dropdowns
 */
export const useGetUser = (orgId: number): UserOption[] => {
  const [users, setUsers] = useState<UserOption[]>([]);
  useEffect(() => {
    apiClient.get(`/rest/users/getByOrg?orgId=${orgId}`).then(({ data }) => {
      const list: UserOption[] = [];
      for (let i = 0; i < data.length; i++) {
        list.push({
          value: data[i].id,
          label: data[i].fullName,
        });
      }
      setUsers(list);
    });
  }, [orgId]);
  return users;
};

export default userClient;

// Export DTOs for external use
export * from "./user.dto";
